import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../utils/http';

/** Admin password reset for one user. The admin shares the new password with the user. */
export default function PasswordResetBox({ userId }: { userId: string }) {
  const { accessToken } = useAuth();
  const [password, setPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const reset = async () => {
    setMessage(null);
    if (password.length < 8) {
      setMessage({ ok: false, text: 'The new password must be at least 8 characters.' });
      return;
    }
    if (!window.confirm('Reset this user\'s password? Their current password will stop working.')) return;
    setIsSaving(true);
    const res = await authFetch(accessToken, `/api/admin/users/${userId}/reset-password`, {
      method: 'POST',
      body: { newPassword: password },
    });
    setIsSaving(false);
    setMessage({ ok: res.ok, text: res.ok ? res.message || 'Password reset.' : res.message });
    if (res.ok) setPassword('');
  };

  return (
    <div className="border border-canvas-300 rounded-2xl p-5">
      <h4 className="font-bold text-brand-950 flex items-center gap-2">
        <FontAwesomeIcon icon={faKey} className="h-4 w-4 text-brand-600" />
        Reset password
      </h4>
      <p className="text-xs text-brand-900/50 mt-1">Sets a new temporary password. Share it with the user so they can sign in.</p>
      <div className="flex flex-col sm:flex-row gap-2 mt-3">
        <input
          type="text"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="New password (min 8 characters)"
          autoComplete="off"
          className="flex-1 px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <button
          onClick={reset}
          disabled={isSaving}
          className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center justify-center gap-2"
        >
          {isSaving && <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />}
          Reset
        </button>
      </div>
      {message && <p className={`text-sm mt-3 ${message.ok ? 'text-green-700' : 'text-red-700'}`}>{message.text}</p>}
    </div>
  );
}
